import { useState, useEffect, useContext, createContext } from "react";
import { baseURL } from "../lib";
import { useToast } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from 'axios'

const AuthContext = createContext()

const AuthProvider = ({ children }) => {

    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    const [isValidSystemConfiguration, setIsValidSystemConfiguration] = useState(false)
    const [auth, setAuth] = useState({
        user: null,
        token: "",
        permissions: [],
    })

    const logout = () => {
        try {
            const data = localStorage.getItem('auth')
            if (data) {
                localStorage.removeItem("auth")
                setAuth({
                    user: null,
                    token: "",
                    permissions: [],
                })
                setIsLoggedIn(false)
                navigate("/")
            }
        } catch (error) {
            console.log(error);
        }
    };

    axios.defaults.headers.common["Authorization"] = auth?.token

    const login = async (credential) => {
        try {
            const { data } = await axios.post(`${baseURL}/admin/user/login`, credential);
            if (data.error) {
                toast({ title: data.message, position: 'top', status: 'error', isClosable: true });
            } else {
                setIsLoggedIn(true)
                setAuth({
                    ...auth,
                    user: data.data.user,
                    token: data.data.token,
                    permissions: data.data.permissions
                })

                toast({ title: data.message, position: 'top', status: 'success', isClosable: true });
                localStorage.setItem('auth', JSON.stringify(data.data))
            }

            return data
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast({ title: 'Please fill all mandatory fields.', position: 'top', status: 'error', isClosable: true });
                    } else {
                        toast({ title: errors[0].msg, position: 'top', status: 'error', isClosable: true });
                    }
                }
            } else {
                toast({ title: error?.response?.data?.message, position: 'top', status: 'error', isClosable: true })
            }
        }
    };

    const forgotPassword = async (email) => {
        try {
            const { data } = await axios.post(`${baseURL}/admin/user/forgot-password`, { email });
            if (data.error === false) {
                toast({ title: data.message, position: 'top', status: 'success', isClosable: true });
            } else {
                toast({ title: data.message, position: 'top', status: 'error', isClosable: true });
            }
            return data
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast({ title: 'Please fill all mandatory fields.', position: 'top', status: 'error', isClosable: true });
                    } else {
                        toast({ title: errors[0].msg, position: 'top', status: 'error', isClosable: true });
                    }
                }
            } else {
                toast({ title: error?.response?.data?.message, position: 'top', status: 'error', isClosable: true })
            }
        }
    }

    const resetPassword = async (token, password) => {
        try {
            const { data } = await axios.put(`${baseURL}/api/user/reset-password?token=${token}`, { password });
            if (data.error === false) {
                setTimeout(function () {
                    toast({ title: data.message, position: 'top', status: 'success', isClosable: true });
                }, 500);
            } else {
                toast({ title: data.message, position: 'top', status: 'error', isClosable: true });
            }
            return data
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast({ title: 'Please fill all mandatory fields.', position: 'top', status: 'error', isClosable: true });
                    } else {
                        toast({ title: errors[0].msg, position: 'top', status: 'error', isClosable: true });
                    }
                }
            } else {
                toast({ title: error?.response?.data?.message, position: 'top', status: 'error', isClosable: true })
            }
        }
    }

    const validateSystemConfiguration = async (password) => {
        try {
            const { data } = await axios.post(`${baseURL}/admin/system/configuration/validate`, { password });
            if (data.error === false) {
                toast({ title: data.message, position: 'top', status: 'success', isClosable: true });
                if (data.data.is_valid_system_configuration) {
                    localStorage.setItem('configuration', JSON.stringify(data.data));
                    setIsValidSystemConfiguration(true)
                    navigate("/login");
                }
            } else {
                toast({ title: data.message, position: 'top', status: 'error', isClosable: true });
            }

            return data
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast({ title: 'Please fill all mandatory fields.', position: 'top', status: 'error', isClosable: true });
                    } else {
                        toast({ title: errors[0].msg, position: 'top', status: 'error', isClosable: true });
                    }
                }
            } else {
                toast({ title: error?.response?.data?.message, position: 'top', status: 'error', isClosable: true })
            }
        }
    }

    useEffect(() => {
        const configuration = localStorage.getItem('configuration')
        if (configuration) {
            const configurationParseData = JSON.parse(configuration)
            configurationParseData.is_valid_system_configuration ? setIsValidSystemConfiguration(true) : setIsValidSystemConfiguration(false)
        }  

        if (!configuration && (location.pathname === '/login' || location.pathname === '/forgot-password')) {
            navigate("/")
        }

        const data = localStorage.getItem("auth");
        if (data && configuration) {
            const parseData = JSON.parse(data);
            setAuth({
                user: parseData.user,
                token: parseData.token,
                permissions: parseData.permissions,
            });
            setIsLoggedIn(true);
        }

        if (!configuration && data) {
            logout();
        }
        
        axios.defaults.headers.common["Authorization"] = auth?.token;

        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response && error.response.status === 401) {
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, [auth.token, isValidSystemConfiguration]);

    return (
        <AuthContext.Provider value={{ auth, login, logout, forgotPassword, resetPassword, validateSystemConfiguration, isLoggedIn, toast, isValidSystemConfiguration, setIsValidSystemConfiguration }}>
            {children}
        </AuthContext.Provider>
    )
}

const useAuth = () => useContext(AuthContext)

export { useAuth, AuthProvider }